import {
  useQuery,
  useQueries,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  type CloudinaryResult,
  getFloorPlanFolders,
  getImageUrlsByFolder,
  getProjectFolders,
} from '@/services/cloudinary';

/*
 * Floor Plans
 */
export type FloorPlan = Record<string, string[]>;
export type FloorPlanGalleryResult = UseQueryResult<FloorPlan, unknown>;

function useGetAllFloorPlanGalleries(
  maxResults = 10,
): FloorPlanGalleryResult[] {
  const { data: folders = [] } = useQuery({
    queryKey: ['floor_plan_folders'],
    queryFn: getFloorPlanFolders,
  });

  return useQueries({
    queries: folders.map(folder => ({
      queryKey: ['floor_plans_gallery', folder, maxResults],
      queryFn: () => getImageUrlsByFolder({ folder, maxResults }),
      enabled: !!folder,
      select: (data: CloudinaryResult) => ({
        [folder.split('/')[1]]: data.resources.map(res => res.public_id),
      }),
    })),
  });
}

/*
 * Projects
 */
export type Project = Record<string, string[]>;
export type ProjectGalleryResult = UseQueryResult<Project, unknown>;

function useGetAllProjectGalleries(maxResults = 10) {
  const { data: folders = [] } = useQuery({
    queryKey: ['project_folders'],
    queryFn: getProjectFolders,
  });

  return useQueries({
    queries: folders.map(folder => ({
      queryKey: ['project_gallery', folder, maxResults],
      queryFn: () => getImageUrlsByFolder({ folder, maxResults }),
      enabled: !!folder,
      select: (data: CloudinaryResult) => ({
        [folder.split('/')[1]]: data.resources.map(res => res.public_id),
      }),
    })),
  });
}

export { useGetAllFloorPlanGalleries, useGetAllProjectGalleries };
